import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Check, Settings } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { conversationService } from "../../services/conversationService";
import { userDisplay } from "../../lib/conversationDisplay";
import { GenerationDetails } from "../../components/admin/GenerationDetails";
import { useAuth } from "../../contexts/AuthContext";
import type {
  ConversationDetailResponse,
  Message,
} from "../../types/conversation";

function MessageBubble({ message }: { message: Message }) {
  const [copied, setCopied] = useState(false);
  const [showGeneration, setShowGeneration] = useState(false);

  const isUser = message.sender === "user";
  const isSystem = message.sender === "system";
  const isAssistant = message.sender === "assistant";

  const extractReasoning = (content: string): string => {
    // Extract reasoning content from between <reasoning></reasoning> tags
    const reasoningMatch = content.match(
      /<reasoning>([\s\S]*?)<\/reasoning>/gi
    );
    if (reasoningMatch && reasoningMatch.length > 0) {
      // Extract the content inside all reasoning tags and combine them
      return reasoningMatch
        .map((match) => match.replace(/<\/?reasoning>/gi, "").trim())
        .join("\n\n")
        .trim();
    }
    return "";
  };

  const removeReasoning = (content: string): string => {
    // Remove reasoning blocks and clean up extra whitespace
    return content
      .replace(/<reasoning>[\s\S]*?<\/reasoning>/gi, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-6`}>
      <div className={`max-w-4xl ${isUser ? "order-2" : "order-1"}`}>
        <div className="flex items-start">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <span
                className={`text-sm font-medium ${
                  isUser
                    ? "text-brand-primary"
                    : isSystem
                    ? "text-brand-muted"
                    : "text-brand-teal"
                }`}
              >
                {isUser ? "User" : isSystem ? "System" : "Assistant"}
              </span>
              <span className="text-xs text-brand-muted">
                {formatDate(message.created_at)}
              </span>
            </div>

            <div
              className={`relative group rounded-lg px-4 py-3 ${
                isUser
                  ? "bg-brand-primary text-white"
                  : isSystem
                  ? "bg-brand-surface text-brand-text border border-brand-border"
                  : "bg-brand-bg/10 text-brand-text border border-brand-border"
              }`}
            >
              {isAssistant ? (
                <div className="prose prose-sm max-w-none prose-gray prose-table:text-xs prose-th:p-2 prose-td:p-2 prose-table:border-collapse">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {removeReasoning(message.content)}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                  {message.content}
                </div>
              )}

              <button
                onClick={handleCopy}
                className={`absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${
                  isUser
                    ? "hover:bg-brand-primary/80 text-white/70 hover:text-white"
                    : isSystem
                    ? "hover:bg-brand-border text-brand-muted"
                    : "hover:bg-brand-bg/20 text-brand-muted"
                }`}
                title="Copy message"
              >
                {copied ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>
            </div>

            {/* Generation Details for Assistant Messages */}
            {isAssistant && message.generation && (
              <div className="mt-3">
                <button
                  onClick={() => setShowGeneration(!showGeneration)}
                  className="flex items-center gap-2 text-xs text-brand-muted hover:text-brand-text transition-colors"
                >
                  <Settings className="w-3 h-3" />
                  Generation Details
                  {showGeneration && (
                    <span className="text-brand-muted">| Click to hide</span>
                  )}
                </button>

                {showGeneration && (
                  <GenerationDetails
                    generation={message.generation}
                    reasoning={extractReasoning(message.content)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ConversationDetailPage(): JSX.Element {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const { clerkUser } = useAuth();
  const [conversationDetail, setConversationDetail] =
    useState<ConversationDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConversationDetail = async () => {
      if (!conversationId) {
        setError("No conversation ID provided");
        setLoading(false);
        return;
      }

      if (!clerkUser) {
        setError("User not authenticated");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await conversationService.getConversationDetail(
          conversationId
        );
        setConversationDetail(response);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load conversation details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchConversationDetail();
  }, [conversationId, clerkUser]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-brand-muted">Loading conversation details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/admin/conversations")}
            className="inline-flex items-center text-sm text-brand-muted hover:text-brand-text"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Conversations
          </button>
        </div>
        <div className="bg-brand-error/10 border border-brand-error/30 rounded-md p-4">
          <div className="text-brand-error">{error}</div>
        </div>
      </div>
    );
  }

  if (!conversationDetail) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/admin/conversations")}
            className="inline-flex items-center text-sm text-brand-muted hover:text-brand-text"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Conversations
          </button>
        </div>
        <div className="text-center text-brand-muted">Conversation not found</div>
      </div>
    );
  }

  const { conversation, messages } = conversationDetail;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/admin/conversations")}
            className="inline-flex items-center text-sm text-brand-muted hover:text-brand-text"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Conversations
          </button>
        </div>
      </div>

      {/* Conversation Info */}
      <div className="bg-brand-bg shadow rounded-card">
        <div className="px-6 py-4 border-b border-brand-border">
          <h1 className="text-xl font-semibold text-brand-text">
            Conversation Details
          </h1>
        </div>
        <div className="px-6 py-4">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-sm font-medium text-brand-muted">
                Conversation ID
              </dt>
              <dd className="mt-1 text-sm text-brand-text font-mono">
                {conversation.id}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-brand-muted">User</dt>
              <dd className="mt-1 text-sm text-brand-text" title={userDisplay(conversation).tooltip}>
                {userDisplay(conversation).label}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-brand-muted">Started At</dt>
              <dd className="mt-1 text-sm text-brand-text">
                {formatDate(conversation.started_at)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-brand-muted">Ended At</dt>
              <dd className="mt-1 text-sm text-brand-text">
                {conversation.ended_at
                  ? formatDate(conversation.ended_at)
                  : "Active"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-brand-muted">
                Total Messages
              </dt>
              <dd className="mt-1 text-sm text-brand-text">{messages.length}</dd>
            </div>
            {conversation.conversation_metadata && (
              <div className="sm:col-span-2 lg:col-span-3">
                <dt className="text-sm font-medium text-brand-muted">Metadata</dt>
                <dd className="mt-1 text-sm text-brand-text">
                  <pre className="text-xs bg-brand-surface p-2 rounded overflow-x-auto">
                    {JSON.stringify(
                      conversation.conversation_metadata,
                      null,
                      2
                    )}
                  </pre>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Messages */}
      <div className="bg-brand-bg shadow rounded-card">
        <div className="px-6 py-4 border-b border-brand-border">
          <h2 className="text-lg font-semibold text-brand-text">
            Chat History ({messages.length} messages)
          </h2>
        </div>
        <div className="px-6 py-6">
          {messages.length === 0 ? (
            <div className="text-center text-brand-muted py-8">
              No messages in this conversation
            </div>
          ) : (
            <div className="space-y-1">
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
